import { runInAction, set } from 'mobx';
import { useTranslation } from 'react-i18next';

import type { OmniCampaign, TOmniIntegration } from '@feathr/blackbox';
import { EIntegrationTypes } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import { useFlags } from '@feathr/extender/state';

import { useChannelAvailability } from '../../CampaignEditPage.useChannelAvailability';
import type { TMetaCardState } from './ChannelsCard.utils';
import { getMetaCardState, getMetaTooltip } from './ChannelsCard.utils';

/**
 * Return type for the useChannelsCard hook.
 */
export interface IUseChannelsCardResult {
  /** Whether Display Advertising is selected */
  displaySelected: boolean;
  /** Whether Meta Advertising is selected */
  metaSelected: boolean;
  /** Handler for Display card selection changes */
  handleDisplayChange: (checked: boolean) => void;
  /** Handler for Meta card selection changes */
  handleMetaChange: (checked: boolean) => void;
  /** Current state of the Meta card (normal, locked, setup, or disabled_monetization) */
  metaCardState: TMetaCardState;
  /** Tooltip text when Meta card is locked or disabled, undefined otherwise */
  metaLockedTooltip: string | undefined;
  /** Whether the Channels card should be shown (metaV2 flag enabled) */
  showChannelsCard: boolean;
  /** Whether channel selection cards should be disabled (campaign is Published/Stopped) */
  isChannelSelectionDisabled: boolean;
}

/**
 * Custom hook that manages state and logic for the ChannelsCard component.
 *
 * Handles:
 * - Channel selection state (display/meta) backed by campaign.integrations
 * - Meta card state based on account tier and integration status
 * - Feature flag gating
 *
 * @param campaign - The OmniCampaign model to read/write channel selections
 * @returns Object containing selection state, handlers, and computed values
 */
export function useChannelsCard(campaign: OmniCampaign): IUseChannelsCardResult {
  const { t } = useTranslation();
  const { metaV2 = false } = useFlags();

  const { displaySelected, metaSelected, isPlatformLite, hasExistingIntegration } =
    useChannelAvailability({ campaign });
  const integrations: TOmniIntegration[] = campaign.get('integrations') ?? [];

  const isChannelSelectionDisabled = campaign.isDisabledCampaignState;

  const metaCardState = getMetaCardState({
    isPlatformLite,
    hasExistingIntegration,
    isMonetization: campaign.isMonetization,
  });

  const metaLockedTooltip = getMetaTooltip(metaCardState, t);

  /**
   * Handles Display card selection changes.
   * Updates campaign.integrations array.
   */
  function handleDisplayChange(checked: boolean): void {
    const wasDualChannel = campaign.isDualChannel;
    const updated = integrations.filter((i) => i !== EIntegrationTypes.Tradedesk);
    if (checked) {
      updated.push(EIntegrationTypes.Tradedesk);
    } else {
      campaign.set({ subadvertiser: undefined });
    }
    campaign.set({ integrations: updated });
    if (wasDualChannel && !campaign.isDualChannel) {
      clearChannelSplit();
    }
  }

  /**
   * Handles Meta card selection changes.
   * Only allows selection when Meta card is in 'normal' state.
   * Clears meta_settings when Meta is deselected.
   */
  function handleMetaChange(checked: boolean): void {
    if (metaCardState !== 'normal') {
      return;
    }
    const wasDualChannel = campaign.isDualChannel;
    const updated = integrations.filter((i) => i !== EIntegrationTypes.Meta);
    if (checked) {
      updated.push(EIntegrationTypes.Meta);
    } else {
      campaign.set({ meta_settings: undefined });
    }
    campaign.set({ integrations: updated });
    if (wasDualChannel && !campaign.isDualChannel) {
      clearChannelSplit();
    }
  }

  function clearChannelSplit(): void {
    const exposureSettings = campaign.get('exposure_settings');
    if (!exposureSettings) {
      return;
    }
    runInAction(() => {
      set(exposureSettings, { channel_split: null });
      campaign.setAttributeDirty('exposure_settings');
    });
    toast(t('Budget split has been reset because a channel was disabled.'), { type: 'warning' });
  }

  return {
    displaySelected,
    metaSelected,
    handleDisplayChange,
    handleMetaChange,
    metaCardState,
    metaLockedTooltip,
    showChannelsCard: metaV2,
    isChannelSelectionDisabled,
  };
}
